import { createContext, ReactNode, useCallback, useContext, useMemo, useRef, useState } from "react";
import { FirestoreInventoryItemProps, FirestoreInventoryMeasurementProps } from "types/auth";
import { useSnackbar } from "notistack";

import ActivityChooseOrCreate from "components/projectElements/activityChooseOrCreate";
import RawWoodInventoryAndMeasurementInput from "components/rawWoodInventoryElements";
import useInventoryData from "hooks/useInventoryData";
import useMapData from "hooks/useMapData";
import { FirestoreActivityProps } from "types/firestoreFeatures";
import { useTranslation } from "utils/locales/utilityFunctions";

type ActivityDialogOptions = {
  activityId?: string;
  projectId?: string;
  existingActivity?: FirestoreActivityProps | { id: string; projectId: string };
  withProjectCreate?: boolean;
  withActivityCreate?: boolean;
  orientationFeature?: boolean;
  withDocking?: boolean;
  onActivityCreated?: (activityId: string) => void;
};

type EditEntityDialogContextValue = {
  openRawWoodInventoryEditor: (inventoryId: string) => void;
  openActivityEditor: (options: ActivityDialogOptions) => Promise<void>;
};

const EditEntityDialogContext = createContext<EditEntityDialogContextValue | null>(null);

export const EditEntityDialogProvider = ({ children }: { children: ReactNode }) => {
  const { getInventoryItemById } = useInventoryData();
  const { activities, fetchActivity, fetchInventoryMeasurements, inventoryMeasurements, getInventoryEditorSnapshot } = useMapData();
  const { enqueueSnackbar } = useSnackbar();
  const { translate } = useTranslation();
  const [inventoryId, setInventoryId] = useState<string | null>(null);
  const [inventorySnapshot, setInventorySnapshot] = useState<FirestoreInventoryItemProps | null>(null);
  const [measurementSnapshot, setMeasurementSnapshot] = useState<FirestoreInventoryMeasurementProps[]>([]);
  const inventoryOpenRequest = useRef(0);
  const [activityDialogOptions, setActivityDialogOptions] = useState<ActivityDialogOptions | null>(null);

  const selectedInventory = inventoryId ? getInventoryItemById(inventoryId) : null;
  const selectedActivity = useMemo(() => {
    if (!activityDialogOptions) return undefined;
    if (activityDialogOptions.existingActivity) return activityDialogOptions.existingActivity;
    if (!activityDialogOptions.activityId) return undefined;

    return Object.values(activities)
      .flatMap((projectActivities) => Object.values(projectActivities))
      .find((activity) => activity.id === activityDialogOptions.activityId);
  }, [activities, activityDialogOptions]);

  const openRawWoodInventoryEditor = useCallback(
    async (nextInventoryId: string) => {
      const request = ++inventoryOpenRequest.current;
      try {
        const snapshot = await getInventoryEditorSnapshot(nextInventoryId);
        if (request !== inventoryOpenRequest.current) return;
        setInventorySnapshot(snapshot.inventory);
        setMeasurementSnapshot(snapshot.measurements);
        if (!inventoryMeasurements[nextInventoryId]) fetchInventoryMeasurements(nextInventoryId);
        setInventoryId(nextInventoryId);
      } catch {
        enqueueSnackbar(translate("inventory-save-conflict"), { variant: "error" });
      }
    },
    [fetchInventoryMeasurements, inventoryMeasurements, getInventoryEditorSnapshot, enqueueSnackbar, translate],
  );

  const openActivityEditor = useCallback(
    async (options: ActivityDialogOptions) => {
      if (options.existingActivity || !options.activityId) {
        setActivityDialogOptions(options);
        return;
      }

      let activity = Object.values(activities)
        .flatMap((projectActivities) => Object.values(projectActivities))
        .find((item) => item.id === options.activityId);

      if (!activity) {
        try {
          activity = await fetchActivity(options.activityId) || undefined;
        } catch {
          activity = undefined;
        }
      }

      if (!activity || (options.projectId && activity.projectId !== options.projectId)) {
        enqueueSnackbar(translate("list-activity-editor-unavailable"), { variant: "error" });
        return;
      }

      setActivityDialogOptions({ ...options, existingActivity: activity });
    },
    [activities, enqueueSnackbar, fetchActivity, translate],
  );

  const value = useMemo(
    () => ({
      openRawWoodInventoryEditor,
      openActivityEditor,
    }),
    [openActivityEditor, openRawWoodInventoryEditor],
  );

  return (
    <EditEntityDialogContext.Provider value={value}>
      {children}

      {selectedInventory && (
        <RawWoodInventoryAndMeasurementInput
          key={selectedInventory.id}
          open={Boolean(inventoryId)}
          selectedInventoryItem={inventorySnapshot ?? selectedInventory}
          liveInventory={selectedInventory}
          measurementData={measurementSnapshot}
          handleClose={() => { inventoryOpenRequest.current++; setInventoryId(null); }}
        />
      )}

      {activityDialogOptions && (
        <ActivityChooseOrCreate
          openExt
          setOpenExt={(open) => {
            if (!open) setActivityDialogOptions(null);
          }}
          existingActivity={selectedActivity as FirestoreActivityProps | undefined}
          withActivityCreate={activityDialogOptions.withActivityCreate}
          withProjectCreate={activityDialogOptions.withProjectCreate}
          orientationFeature={activityDialogOptions.orientationFeature}
          withDocking={activityDialogOptions.withDocking}
          onActivityCreated={activityDialogOptions.onActivityCreated}
        />
      )}
    </EditEntityDialogContext.Provider>
  );
};

export const useEditEntityDialogs = () => {
  const context = useContext(EditEntityDialogContext);
  if (!context) throw new Error("useEditEntityDialogs must be used within EditEntityDialogProvider");
  return context;
};
